import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { JiraIssue, SessionJiraSync } from '../types/jira';
import { jiraApi } from '../services/jiraApi';
import './JiraIntegration.css';

interface JiraIntegrationProps {
  sessionId: string;
  storyTitle: string;
  averagePoints: number;
  onSyncComplete?: (sync: SessionJiraSync) => void;
}

const JiraIntegration: React.FC<JiraIntegrationProps> = ({
  sessionId,
  storyTitle,
  averagePoints,
  onSyncComplete,
}) => {
  const { t } = useTranslation();
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const [linkedIssue, setLinkedIssue] = useState<JiraIssue | null>(null);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isLinking, setIsLinking] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showLinkInput, setShowLinkInput] = useState<boolean>(false);
  const [issueKeyInput, setIssueKeyInput] = useState<string>('');

  useEffect(() => {
    // JIRA連携が有効かチェック
    setIsEnabled(jiraApi.isEnabled());
  }, []);

  const handleCreateTicket = async () => {
    setIsCreating(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const config = jiraApi.getConfig();
      if (!config) {
        throw new Error('JIRA is not configured');
      }

      // 新規チケット作成
      const sync = await jiraApi.syncSessionToJira(sessionId, {
        projectKey: config.projectKey,
        issueType: config.issueType,
        createNew: true,
      });

      // 作成されたチケット情報を取得
      const issue = await jiraApi.getIssue(sync.issueKey);
      setLinkedIssue(issue);
      setSuccessMessage(
        t('jira.integration.ticketCreated') || `Ticket ${sync.issueKey} created successfully`
      );

      if (onSyncComplete) {
        onSyncComplete(sync);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create ticket');
    } finally {
      setIsCreating(false);
    }
  };

  const handleLinkTicket = async () => {
    if (!issueKeyInput.trim()) {
      setError(t('jira.integration.enterIssueKey') || 'Please enter an issue key');
      return;
    }

    setIsLinking(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // 既存チケットに同期
      const sync = await jiraApi.syncSessionToJira(sessionId, {
        issueKey: issueKeyInput.trim(),
        createNew: false,
      });

      // チケット情報を取得
      const issue = await jiraApi.getIssue(sync.issueKey);
      setLinkedIssue(issue);
      setSuccessMessage(
        t('jira.integration.ticketLinked') || `Linked to ticket ${sync.issueKey} successfully`
      );
      setShowLinkInput(false);
      setIssueKeyInput('');

      if (onSyncComplete) {
        onSyncComplete(sync);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link ticket');
    } finally {
      setIsLinking(false);
    }
  };

  const handleViewTicket = () => {
    if (linkedIssue) {
      const config = jiraApi.getConfig();
      if (config) {
        const url = `https://${config.domain}/browse/${linkedIssue.key}`;
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    }
  };

  if (!isEnabled) {
    return (
      <div className="jira-integration disabled">
        <p className="jira-disabled-message">
          {t('jira.integration.notConfigured') || 'JIRA integration is not configured'}
        </p>
      </div>
    );
  }

  return (
    <div className="jira-integration">
      <h4 className="jira-integration-title">
        {t('jira.integration.title') || 'JIRA Integration'}
      </h4>
      <p className="jira-integration-story">{storyTitle}</p>

      {error && (
        <div className="jira-message error">
          <span className="message-icon">✗</span>
          <span>{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="jira-message success">
          <span className="message-icon">✓</span>
          <span>{successMessage}</span>
        </div>
      )}

      {linkedIssue ? (
        <div className="linked-ticket">
          <div className="ticket-info">
            <span className="ticket-key">{linkedIssue.key}</span>
            <span className="ticket-summary">{linkedIssue.summary}</span>
            {linkedIssue.storyPoints !== undefined && (
              <span className="ticket-points">{linkedIssue.storyPoints} pts</span>
            )}
          </div>
          <button onClick={handleViewTicket} className="btn btn-secondary btn-sm">
            {t('jira.integration.viewTicket') || 'View'}
          </button>
        </div>
      ) : (
        <div className="jira-actions">
          <button
            onClick={handleCreateTicket}
            disabled={isCreating}
            className="btn btn-primary"
          >
            {isCreating
              ? t('jira.integration.creating') || 'Creating...'
              : t('jira.integration.createTicket') || 'Create JIRA Ticket'}
          </button>

          {!showLinkInput ? (
            <button
              onClick={() => setShowLinkInput(true)}
              className="btn btn-secondary"
            >
              {t('jira.integration.linkExisting') || 'Link Existing Ticket'}
            </button>
          ) : (
            <div className="link-input-group">
              <input
                type="text"
                value={issueKeyInput}
                onChange={(e) => setIssueKeyInput(e.target.value)}
                placeholder="PROJ-123"
                className="issue-key-input"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleLinkTicket();
                  }
                }}
              />
              <button
                onClick={handleLinkTicket}
                disabled={isLinking || !issueKeyInput.trim()}
                className="btn btn-primary btn-sm"
              >
                {isLinking ? t('jira.integration.linking') || 'Linking...' : t('jira.integration.link') || 'Link'}
              </button>
              <button
                onClick={() => {
                  setShowLinkInput(false);
                  setIssueKeyInput('');
                  setError(null);
                }}
                className="btn btn-secondary btn-sm"
              >
                {t('jira.integration.cancel') || 'Cancel'}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="jira-info">
        <p className="info-item">
          <strong>{t('jira.integration.storyPoints') || 'Story Points'}:</strong>{' '}
          {averagePoints.toFixed(1)}
        </p>
      </div>
    </div>
  );
};

export default JiraIntegration;
